import React, { useState } from "react";
import { jsPDF } from "jspdf";
import loadImage from "blueimp-load-image";
import { db } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { initializeApp } from "firebase/app";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import ImageOptimizer from "./imageOptimizer";
import ImageAnalyzer from "./imageAnalyzer";

// Initialize storage with explicit bucket
const firebaseConfig = {
  apiKey: "AIzaSyDWRy71b7QrAU8owggWNa1mQpqtjiiJBDQ",
  authDomain: "agentmiatest.firebaseapp.com",
  projectId: "agentmiatest",
  storageBucket: "agentmiatest.firebasestorage.app",
  messagingSenderId: "97168887618",
  appId: "1:97168887618:web:2a3da7390911df0fab82db"
};

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

const ReportBuilder = () => {
  const [reportTitle, setReportTitle] = useState("");
  const [projectManager, setProjectManager] = useState("");
  const [reportDate, setReportDate] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [sections, setSections] = useState([{ name: "Section 1", images: [] }]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [autoAnalyzeEnabled, setAutoAnalyzeEnabled] = useState(false);

  // Initialize utilities
  const imageOptimizer = new ImageOptimizer();
  const imageAnalyzer = new ImageAnalyzer();

  const showStatus = (message, type = "info") => {
    setStatusMessage({ text: message, type });
    console.log(message);
  };

  const clearStatus = () => {
    setStatusMessage("");
  };

  const analyzeImageDescription = async (file, sectionName) => {
    if (!autoAnalyzeEnabled || !imageAnalyzer.isConfigured()) {
      return null;
    }

    try {
      setIsAnalyzing(true);
      showStatus(`🤖 Analyzing image for description...`, "info");

      const context = {
        projectType: "construction",
        sectionName: sectionName,
        reportTitle: reportTitle,
        clientName: clientName
      };

      const result = await imageAnalyzer.analyzeImage(file, context);
      
      if (result.success) {
        showStatus(`✅ AI description generated successfully!`, "success");
        return result.description;
      } else {
        showStatus(`⚠️ AI analysis failed, using fallback description`, "warning");
        return result.fallbackDescription;
      }
    } catch (error) {
      console.error("Error analyzing image:", error);
      showStatus(`⚠️ AI analysis error: ${error.message}`, "warning");
      return imageAnalyzer.generateFallbackDescription(file);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const optimizeAndProcessImage = async (file) => {
    try {
      setIsOptimizing(true);
      showStatus(`🔄 Optimizing image: ${file.name}...`, "info");
      
      // Check if it's an image file
      if (!imageOptimizer.isImageFile(file)) {
        throw new Error("Selected file is not an image");
      }

      // Get optimal settings for this image
      const settings = imageOptimizer.getOptimalSettings(file);
      
      // Optimize the image
      const optimizedFile = await imageOptimizer.optimizeImage(file, settings);
      
      showStatus(`✅ Image optimized successfully! Size reduced by ${(((file.size - optimizedFile.size) / file.size) * 100).toFixed(1)}%`, "success");
      
      return optimizedFile;
    } catch (error) {
      console.error("Error optimizing image:", error);
      showStatus(`⚠️ Image optimization failed, using original: ${error.message}`, "warning");
      return file; // Return original file if optimization fails
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleImageChange = async (e, sectionIndex) => {
    const files = Array.from(e.target.files);
    const updatedSections = [...sections];
    const currentSection = updatedSections[sectionIndex];
    
    for (const file of files) {
      try {
        // Optimize the image before processing
        const optimizedFile = await optimizeAndProcessImage(file);
        
        // Generate AI description
        const aiDescription = await analyzeImageDescription(optimizedFile, currentSection.name);
        
        const reader = new FileReader();
        reader.onload = (event) => {
          updatedSections[sectionIndex].images.push({
            file: optimizedFile, // Use optimized file
            url: event.target.result,
            caption: aiDescription || "", // Use AI-generated description
            originalSize: file.size,
            optimizedSize: optimizedFile.size,
            compressionRatio: ((file.size - optimizedFile.size) / file.size * 100).toFixed(1),
            aiGenerated: !!aiDescription,
            analysisTimestamp: new Date().toISOString()
          });
          setSections([...updatedSections]);
        };
        reader.readAsDataURL(optimizedFile);
      } catch (error) {
        console.error("Error processing image:", error);
        showStatus(`❌ Failed to process ${file.name}: ${error.message}`, "error");
      }
    }
  };

  const handleReplaceImage = async (sectionIndex, imageIndex, file) => {
    if (!file) return;
    
    try {
      // Optimize the replacement image
      const optimizedFile = await optimizeAndProcessImage(file);
      
      // Generate AI description for replacement
      const currentSection = sections[sectionIndex];
      const aiDescription = await analyzeImageDescription(optimizedFile, currentSection.name);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const updatedSections = [...sections];
        updatedSections[sectionIndex].images[imageIndex] = {
          file: optimizedFile, // Use optimized file
          url: event.target.result,
          caption: aiDescription || updatedSections[sectionIndex].images[imageIndex].caption,
          originalSize: file.size,
          optimizedSize: optimizedFile.size,
          compressionRatio: ((file.size - optimizedFile.size) / file.size * 100).toFixed(1),
          aiGenerated: !!aiDescription,
          analysisTimestamp: new Date().toISOString()
        };
        setSections(updatedSections);
      };
      reader.readAsDataURL(optimizedFile);
    } catch (error) {
      console.error("Error replacing image:", error);
      showStatus(`❌ Failed to replace image: ${error.message}`, "error");
    }
  };

  const regenerateDescription = async (sectionIndex, imageIndex) => {
    try {
      const currentSection = sections[sectionIndex];
      const currentImage = currentSection.images[imageIndex];
      
      setIsAnalyzing(true);
      showStatus(`🤖 Regenerating AI description...`, "info");
      
      const context = {
        projectType: "construction",
        sectionName: currentSection.name,
        reportTitle: reportTitle,
        clientName: clientName
      };

      const result = await imageAnalyzer.analyzeImage(currentImage.file, context);
      
      if (result.success) {
        const updatedSections = [...sections];
        updatedSections[sectionIndex].images[imageIndex].caption = result.description;
        updatedSections[sectionIndex].images[imageIndex].aiGenerated = true;
        updatedSections[sectionIndex].images[imageIndex].analysisTimestamp = new Date().toISOString();
        setSections(updatedSections);
        showStatus(`✅ AI description regenerated successfully!`, "success");
      } else {
        showStatus(`❌ Failed to regenerate description: ${result.error}`, "error");
      }
    } catch (error) {
      console.error("Error regenerating description:", error);
      showStatus(`❌ Failed to regenerate description: ${error.message}`, "error");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRemoveImage = (sectionIndex, imageIndex) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].images.splice(imageIndex, 1);
    setSections(updatedSections);
  };

  const handleCaptionChange = (sectionIndex, imageIndex, caption) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].images[imageIndex].caption = caption;
    updatedSections[sectionIndex].images[imageIndex].aiGenerated = false; // Mark as manually edited
    setSections(updatedSections);
  };

  const handleSectionNameChange = (index, name) => {
    const updatedSections = [...sections];
    updatedSections[index].name = name;
    setSections(updatedSections);
  };

  const addSection = () => {
    setSections([...sections, { name: `Section ${sections.length + 1}`, images: [] }]);
  };

  const saveReportToFirestore = async () => {
    if (!reportTitle.trim()) {
      showStatus("❌ Project Title is required!", "error");
      return;
    }

    setIsSaving(true);
    clearStatus();
    
    try {
      showStatus("🔄 Starting save process...", "info");
      
      // Calculate total storage savings
      let totalOriginalSize = 0;
      let totalOptimizedSize = 0;
      sections.forEach(section => {
        section.images.forEach(img => {
          totalOriginalSize += img.originalSize || img.file.size;
          totalOptimizedSize += img.optimizedSize || img.file.size;
        });
      });
      
      if (totalOriginalSize > 0) {
        const totalSavings = ((totalOriginalSize - totalOptimizedSize) / totalOriginalSize * 100).toFixed(1);
        showStatus(`📊 Total storage savings: ${totalSavings}% (${(totalOriginalSize / 1024 / 1024).toFixed(2)}MB → ${(totalOptimizedSize / 1024 / 1024).toFixed(2)}MB)`, "info");
      }
      
      // Process sections and upload images
      const sectionUploads = await Promise.all(
        sections.map(async (section, sectionIndex) => {
          showStatus(`🔄 Processing section ${sectionIndex + 1}: ${section.name}`, "info");
          
          const uploadedImages = await Promise.all(
            section.images.map(async (img, imgIndex) => {
              showStatus(`🔄 Uploading optimized image ${imgIndex + 1} in section ${sectionIndex + 1}`, "info");
              
              try {
                const timestamp = Date.now();
                const fileName = `image-${sectionIndex}-${imgIndex}-${timestamp}`;
                const storageRef = ref(storage, `photoReports/${section.name}/${fileName}`);
                
                console.log(`Uploading optimized image to: gs://agentmiatest.firebasestorage.app/photoReports/${section.name}/${fileName}`);
                console.log(`Image size: ${(img.file.size / 1024 / 1024).toFixed(2)}MB (${img.compressionRatio || 0}% compression)`);
                
                await uploadBytes(storageRef, img.file);
                const downloadURL = await getDownloadURL(storageRef);
                
                showStatus(`✅ Optimized image ${imgIndex + 1} uploaded successfully`, "success");
                
                return {
                  caption: img.caption,
                  imageUrl: downloadURL,
                  originalSize: img.originalSize,
                  optimizedSize: img.optimizedSize,
                  compressionRatio: img.compressionRatio,
                  aiGenerated: img.aiGenerated,
                  analysisTimestamp: img.analysisTimestamp
                };
              } catch (error) {
                console.error(`Error uploading image ${imgIndex + 1}:`, error);
                showStatus(`❌ Failed to upload image ${imgIndex + 1}: ${error.message}`, "error");
                throw error;
              }
            })
          );
          
          return {
            name: section.name,
            images: uploadedImages,
          };
        })
      );

      showStatus("🔄 Saving to Firestore...", "info");
      
      const reportData = {
        title: reportTitle,
        createdBy: projectManager,
        dateCreated: serverTimestamp(),
        reportDate: reportDate,
        sections: sectionUploads,
        clientName,
        projectAddress,
        companyName,
        optimizationStats: {
          totalOriginalSize,
          totalOptimizedSize,
          totalSavings: totalOriginalSize > 0 ? ((totalOriginalSize - totalOptimizedSize) / totalOriginalSize * 100).toFixed(1) : 0
        },
        aiAnalysisEnabled: autoAnalyzeEnabled
      };
      
      console.log("Report data to save:", reportData);
      
      const docRef = await addDoc(collection(db, "reports"), reportData);
      
      showStatus(`✅ Report saved successfully! ID: ${docRef.id}`, "success");
      console.log("Report saved with ID:", docRef.id);
      
    } catch (error) {
      console.error("Error saving report:", error);
      showStatus(`❌ Failed to save report: ${error.message}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const loadLogos = async () => {
    const loadSingleLogo = (paths, logoType) => {
      return new Promise((resolve) => {
        const logo = new Image();
        let currentPathIndex = 0;
        
        const tryNextPath = () => {
          if (currentPathIndex >= paths.length) {
            console.warn(`⚠️ ${logoType} logo not found, continuing without logo`);
            showStatus(`⚠️ ${logoType} logo not found, continuing without logo`, "warning");
            resolve(null);
            return;
          }
          
          logo.src = paths[currentPathIndex];
          currentPathIndex++;
        };
        
        logo.onload = () => {
          console.log(`✅ ${logoType} logo loaded successfully`);
          resolve(logo);
        };
        
        logo.onerror = () => {
          console.warn(`⚠️ ${logoType} logo loading failed, trying next path...`);
          tryNextPath();
        };
        
        // Start with first path
        tryNextPath();
        
        // Fallback timeout
        setTimeout(() => {
          if (!logo.complete) {
            console.warn(`⚠️ ${logoType} logo loading timeout, continuing without logo`);
            showStatus(`⚠️ ${logoType} logo loading timeout, continuing without logo`, "warning");
            resolve(null);
          }
        }, 5000);
      });
    };

    // Load cover page logo (GEP-Logo-24.jpg)
    const coverLogoPaths = [
      "/GEP-Logo-24.jpg",
      "./GEP-Logo-24.jpg",
      `${process.env.PUBLIC_URL || ""}/GEP-Logo-24.jpg`,
      "/GEP-Logo-24-resized.jpg"
    ];

    // Load content pages logo (GEP-Logo.png)
    const contentLogoPaths = [
      "/GEP-Logo.png",
      "./GEP-Logo.png",
      `${process.env.PUBLIC_URL || ""}/GEP-Logo.png`
    ];

    const [coverLogo, contentLogo] = await Promise.all([
      loadSingleLogo(coverLogoPaths, "Cover"),
      loadSingleLogo(contentLogoPaths, "Content")
    ]);

    return { coverLogo, contentLogo };
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    clearStatus();
    
    try {
      showStatus("🔄 Generating PDF...", "info");
      
      const pdf = new jsPDF();
      const { coverLogo, contentLogo } = await loadLogos();
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxImgWidth = 160;
      const maxImgHeight = 100;
      let currentPageNumber = 1;

      // Helper function to add page number
      const addPageNumber = () => {
        pdf.setFontSize(10);
        pdf.setFont("Helvetica", "normal");
        pdf.text(`Page ${currentPageNumber}`, pageWidth - 25, pageHeight - 10);
        currentPageNumber++;
      };

      // Helper function to add GEP logo to content pages (pages 2 onward)
      const addLogoToContentPage = () => {
        if (contentLogo && currentPageNumber > 1) {
          const logoWidth = 30; // Smaller size for content pages
          const logoAspectRatio = contentLogo.width / contentLogo.height;
          const logoHeight = logoWidth / logoAspectRatio;
          const logoX = 10; // Top left corner
          const logoY = 10; // Top left corner
          pdf.addImage(contentLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
        }
      };

      // Title page with GEP cover logo (GEP-Logo-24.jpg)
      if (coverLogo) {
        const logoWidth = 80;
        const logoAspectRatio = coverLogo.width / coverLogo.height;
        const logoHeight = logoWidth / logoAspectRatio;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = (pageHeight - logoHeight) / 2 - 50;
        pdf.addImage(coverLogo, "JPEG", logoX, logoY, logoWidth, logoHeight);
      }

      // Title page content
      pdf.setFont("Helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text(`Project Title: ${reportTitle}`, 20, pageHeight - 60);

      pdf.setFontSize(12);
      const fields = [
        `Project Manager: ${projectManager}`,
        `Date of Report: ${reportDate}`,
        `Client Name: ${clientName}`,
        `Project Address: ${projectAddress}`,
        `Prepared By: ${companyName || "GEP Contractors"}`,
      ];
      fields.forEach((text, i) => {
        pdf.text(text, 20, pageHeight - 50 + i * 8);
      });

      // Add page number to title page
      addPageNumber();

      // Process sections (ADD logo on content pages starting from page 2)
      for (const section of sections) {
        pdf.addPage();
        
        // Add GEP logo to content page (page 2 onward)
        addLogoToContentPage();
        
        // Section title
        pdf.setFontSize(16);
        pdf.setFont("Helvetica", "bold");
        pdf.text(section.name, pageWidth / 2, 30, { align: "center" });

        let yOffset = 50;
        let imageCounter = 0;

        for (let i = 0; i < section.images.length; i++) {
          if (imageCounter === 2) {
            // Add page number before creating new page
            addPageNumber();
            
            pdf.addPage();
            yOffset = 50;
            imageCounter = 0;
            
            // Add GEP logo to continuation page
            addLogoToContentPage();
            
            // Section title for continuation page
            pdf.setFontSize(16);
            pdf.setFont("Helvetica", "bold");
            pdf.text(`${section.name} (continued)`, pageWidth / 2, 30, { align: "center" });
          }

          pdf.setFontSize(12);
          pdf.text(section.images[i].caption || " ", pageWidth / 2, yOffset, { align: "center" });

          const loadResult = await loadImage(section.images[i].file, {
            canvas: true,
            orientation: true,
          });
          const canvas = loadResult.image;
          const imgData = canvas.toDataURL("image/jpeg");

          const aspectRatio = canvas.width / canvas.height;
          let imgWidth = maxImgWidth;
          let imgHeight = imgWidth / aspectRatio;
          if (imgHeight > maxImgHeight) {
            imgHeight = maxImgHeight;
            imgWidth = imgHeight * aspectRatio;
          }
          const centeredX = (pageWidth - imgWidth) / 2;

          pdf.addImage(imgData, "JPEG", centeredX, yOffset + 5, imgWidth, imgHeight);
          yOffset += imgHeight + 20;
          imageCounter++;
        }

        // Add page number to section page
        addPageNumber();
      }

      showStatus("✅ PDF generated successfully", "success");
      pdf.save("GEP_Photo_Report.pdf");
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showStatus(`❌ Failed to generate PDF: ${error.message}`, "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const getStatusStyle = (type) => {
    const baseStyle = {
      padding: "10px",
      marginBottom: "20px",
      borderRadius: "5px",
      fontWeight: "bold"
    };
    
    switch (type) {
      case "success":
        return { ...baseStyle, backgroundColor: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" };
      case "error":
        return { ...baseStyle, backgroundColor: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" };
      case "warning":
        return { ...baseStyle, backgroundColor: "#fff3cd", color: "#856404", border: "1px solid #ffeaa7" };
      default:
        return { ...baseStyle, backgroundColor: "#d1ecf1", color: "#0c5460", border: "1px solid #bee5eb" };
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial" }}>
      {/* GEP Header with Logo */}
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        marginBottom: "30px",
        padding: "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "10px",
        border: "2px solid #dee2e6"
      }}>
        <img 
          src="/GEP-Logo-24.jpg" 
          alt="GEP Contractors Logo" 
          style={{ 
            height: "60px", 
            marginRight: "20px",
            objectFit: "contain"
          }}
          onError={(e) => {
            // Try PNG version if JPG fails
            e.target.src = "/GEP-Logo.png";
            e.target.onerror = () => {
              e.target.style.display = "none";
            };
          }}
        />
        <div>
          <h1 style={{ 
            margin: "0 0 5px 0", 
            color: "#333",
            fontSize: "28px",
            fontWeight: "bold"
          }}>
            GEP Photo Report Generator
          </h1>
          <p style={{ 
            margin: 0, 
            color: "#666",
            fontSize: "14px"
          }}>
            Professional Photo Reports with AI Analysis & Image Optimization
          </p>
        </div>
      </div>

      {/* AI Analysis Settings */}
      <div style={{ 
        marginBottom: "20px", 
        border: "1px solid #17a2b8", 
        padding: "15px", 
        borderRadius: "5px",
        backgroundColor: "#d1ecf1"
      }}>
        <h3 style={{ margin: "0 0 10px 0", color: "#0c5460" }}>🤖 AI Image Analysis</h3>
        <div style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input 
              type="checkbox" 
              checked={autoAnalyzeEnabled} 
              onChange={(e) => setAutoAnalyzeEnabled(e.target.checked)}
              style={{ transform: "scale(1.2)" }}
            />
            <span style={{ fontSize: "14px", fontWeight: "bold" }}>
              Auto-generate descriptions for uploaded images
            </span>
          </label>
          <div style={{ 
            fontSize: "12px", 
            color: "#0c5460",
            padding: "5px 10px",
            backgroundColor: "rgba(255,255,255,0.7)",
            borderRadius: "3px"
          }}>
            Status: {imageAnalyzer.isConfigured() ? "✅ AI Ready" : "❌ API Not Configured"}
          </div>
        </div>
        <p style={{ 
          margin: "8px 0 0 0", 
          fontSize: "12px", 
          color: "#0c5460",
          fontStyle: "italic"
        }}>
          When enabled, AI will automatically analyze each uploaded image and generate professional descriptions suitable for construction reports.
        </p>
      </div>

      {/* Status Message */}
      {statusMessage && (
        <div style={getStatusStyle(statusMessage.type)}>
          {statusMessage.text}
        </div>
      )}

      {/* Processing Status */}
      {(isOptimizing || isAnalyzing) && (
        <div style={getStatusStyle("info")}>
          {isOptimizing && "🔄 Optimizing image... "}
          {isAnalyzing && "🤖 Analyzing image for description... "}
          Please wait.
        </div>
      )}

      {/* Form fields */}
      <div style={{ 
        marginBottom: "20px", 
        border: "1px solid #ccc", 
        padding: "20px", 
        borderRadius: "5px",
        backgroundColor: "#f9f9f9"
      }}>
        <h3>Report Title Page</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <input 
            type="text" 
            placeholder="Project Title *" 
            value={reportTitle} 
            onChange={(e) => setReportTitle(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
            required
          />
          <input 
            type="text" 
            placeholder="Project Manager" 
            value={projectManager} 
            onChange={(e) => setProjectManager(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Date of Report" 
            value={reportDate} 
            onChange={(e) => setReportDate(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Client Name" 
            value={clientName} 
            onChange={(e) => setClientName(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Project Address" 
            value={projectAddress} 
            onChange={(e) => setProjectAddress(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
          <input 
            type="text" 
            placeholder="Prepared By" 
            value={companyName} 
            onChange={(e) => setCompanyName(e.target.value)}
            style={{ padding: "8px", border: "1px solid #ddd", borderRadius: "3px" }}
          />
        </div>
      </div>

      {/* Sections */}
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex} style={{ 
          border: "1px solid #ccc", 
          marginBottom: "30px", 
          padding: "20px",
          borderRadius: "5px",
          backgroundColor: "#f9f9f9"
        }}>
          <h3>{section.name}</h3>
          <input 
            type="text" 
            value={section.name} 
            onChange={(e) => handleSectionNameChange(sectionIndex, e.target.value)}
            style={{ 
              width: "100%", 
              padding: "8px", 
              marginBottom: "15px",
              border: "1px solid #ddd", 
              borderRadius: "3px",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          />
          
          {section.images.map((img, imgIndex) => (
            <div key={imgIndex} style={{ 
              marginBottom: "20px", 
              padding: "15px", 
              border: "1px solid #ddd",
              borderRadius: "5px",
              backgroundColor: "white"
            }}>
              {/* AI-generated indicator */}
              {img.aiGenerated && (
                <div style={{ 
                  fontSize: "11px", 
                  color: "#17a2b8", 
                  marginBottom: "8px",
                  padding: "4px 8px",
                  backgroundColor: "#d1ecf1",
                  borderRadius: "3px",
                  display: "inline-block"
                }}>
                  🤖 AI-Generated Description
                </div>
              )}
              
              <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
                <input 
                  type="text" 
                  placeholder="Picture Description" 
                  value={img.caption} 
                  onChange={(e) => handleCaptionChange(sectionIndex, imgIndex, e.target.value)}
                  style={{ 
                    flex: 1,
                    padding: "8px", 
                    border: "1px solid #ddd", 
                    borderRadius: "3px"
                  }}
                />
                {imageAnalyzer.isConfigured() && (
                  <button
                    onClick={() => regenerateDescription(sectionIndex, imgIndex)}
                    disabled={isAnalyzing}
                    style={{
                      backgroundColor: "#17a2b8",
                      color: "white",
                      border: "none",
                      padding: "8px 12px",
                      borderRadius: "3px",
                      fontSize: "12px",
                      cursor: isAnalyzing ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap"
                    }}
                  >
                    🤖 Regenerate
                  </button>
                )}
              </div>
              
              {/* Image optimization info */}
              {img.compressionRatio && (
                <div style={{ 
                  fontSize: "12px", 
                  color: "#28a745", 
                  marginBottom: "10px",
                  padding: "5px",
                  backgroundColor: "#d4edda",
                  borderRadius: "3px"
                }}>
                  📊 Optimized: {img.compressionRatio}% smaller 
                  ({formatFileSize(img.originalSize)} → {formatFileSize(img.optimizedSize)})
                </div>
              )}
              
              <div style={{ marginBottom: "10px" }}>
                <button 
                  onClick={() => document.getElementById(`replace-${sectionIndex}-${imgIndex}`).click()}
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "3px",
                    marginRight: "10px",
                    cursor: "pointer"
                  }}
                  disabled={isOptimizing || isAnalyzing}
                >
                  Replace Image
                </button>
                <input 
                  type="file" 
                  id={`replace-${sectionIndex}-${imgIndex}`} 
                  accept="image/*" 
                  style={{ display: "none" }} 
                  onChange={(e) => handleReplaceImage(sectionIndex, imgIndex, e.target.files[0])}
                />
                <button 
                  onClick={() => handleRemoveImage(sectionIndex, imgIndex)}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "8px 15px",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }}
                >
                  Remove Image
                </button>
              </div>
              <img 
                src={img.url} 
                alt="Preview" 
                style={{ 
                  maxWidth: "300px", 
                  maxHeight: "200px", 
                  border: "1px solid #ddd",
                  borderRadius: "3px"
                }} 
              />
            </div>
          ))}
          
          <div style={{ marginTop: "15px" }}>
            <button 
              onClick={() => document.getElementById(`file-${sectionIndex}`).click()}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "10px 20px",
                borderRadius: "3px",
                cursor: "pointer",
                fontSize: "14px"
              }}
              disabled={isOptimizing || isAnalyzing}
            >
              {isOptimizing || isAnalyzing ? "Processing..." : "Add Images (Auto-Optimized & AI-Analyzed)"}
            </button>
            <input 
              type="file" 
              id={`file-${sectionIndex}`} 
              accept="image/*" 
              multiple 
              style={{ display: "none" }} 
              onChange={(e) => handleImageChange(e, sectionIndex)}
            />
          </div>
        </div>
      ))}

      {/* Action buttons */}
      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <button 
          onClick={addSection}
          style={{
            backgroundColor: "#ffc107",
            color: "black",
            border: "none",
            padding: "12px 25px",
            borderRadius: "5px",
            marginRight: "15px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "bold"
          }}
        >
          Add Section
        </button>
        
        <button 
          onClick={generatePDF}
          disabled={isGenerating || isOptimizing || isAnalyzing}
          style={{
            backgroundColor: isGenerating ? "#6c757d" : "#17a2b8",
            color: "white",
            border: "none",
            padding: "12px 25px",
            borderRadius: "5px",
            marginRight: "15px",
            cursor: isGenerating ? "not-allowed" : "pointer",
            fontSize: "16px",
            fontWeight: "bold"
          }}
        >
          {isGenerating ? "Generating..." : "Generate PDF Report"}
        </button>
        
        <button 
          onClick={saveReportToFirestore}
          disabled={isSaving || isOptimizing || isAnalyzing || !reportTitle.trim()}
          style={{
            backgroundColor: isSaving || !reportTitle.trim() ? "#6c757d" : "#28a745",
            color: "white",
            border: "none",
            padding: "12px 25px",
            borderRadius: "5px",
            cursor: isSaving || !reportTitle.trim() ? "not-allowed" : "pointer",
            fontSize: "16px",
            fontWeight: "bold"
          }}
        >
          {isSaving ? "Saving..." : "Save Report"}
        </button>
      </div>

      {/* Feature info */}
      <div style={{ 
        marginTop: "20px", 
        padding: "15px", 
        backgroundColor: "#e9ecef", 
        borderRadius: "5px",
        fontSize: "14px"
      }}>
        <h4>🚀 GEP Photo Report Generator Features:</h4>
        <ul>
          <li>✅ <strong>AI Image Analysis:</strong> Automatic professional descriptions for construction photos</li>
          <li>✅ <strong>Manual Override:</strong> Edit AI descriptions or disable auto-analysis</li>
          <li>✅ <strong>Image Optimization:</strong> 50-80% smaller files with maintained quality</li>
          <li>✅ <strong>Professional PDFs:</strong> GEP logo on cover page, page numbers throughout</li>
          <li>✅ <strong>Smart Context:</strong> AI considers project details for better descriptions</li>
          <li>✅ <strong>Regenerate Descriptions:</strong> Get new AI descriptions anytime</li>
        </ul>
      </div>
    </div>
  );
};

export default ReportBuilder;
